import { $t } from '@/i18n';
import { cloneDeep, set } from 'lodash';
import { getAvailableComponent } from '../comps-loader';
import { globalConstantService } from '@/workbench/modules/services/global-constant';
import { SSO_DEPLOY } from '@/config/constant';
import getUtilsSchema from './utils-schema';
import getFlowSchema from './flow-schema';
import getOrgSchema from './org-schema';
// 预设字段对应的组件
const COMP = {
    readonlyText: 'w-readonly',
    boolean: 'w-boolean',
    jsonText: 'w-json',
    avatar: 'w-avatar',
    image: 'w-image',
};
const fitSchemaFieldUIType = (schema) => {
    // 已经在 schema 定义 ui 的字段不做处理
    if (schema.ui)
        return schema;
    // 只读字符串
    if (schema.type === 'string' && schema.readonly) {
        set(schema, 'ui.type', getAvailableComponent('public', COMP.readonlyText));
    }
    // 布尔值
    if (schema.type === 'boolean') {
        set(schema, 'ui.type', getAvailableComponent('public', COMP.boolean));
        if (schema.readonly) {
            set(schema, 'ui.props.disabled', true);
        }
    }
    // 只读对象/数组
    if (schema.type && ['object', 'array'].includes(schema.type) && schema.readonly) {
        set(schema, 'ui.type', getAvailableComponent('public', COMP.jsonText));
        set(schema, 'ui.props.readonly', true);
    }
    if (schema.type === 'object' && Array.isArray(schema.fields)) {
        schema.fields.forEach(field => fitSchemaFieldUIType(field));
    }
    return schema;
};
const getNavigateArguments = () => [
    {
        id: 'option',
        type: 'object',
        description: $t('k_d4ee9699'),
        fields: [
            {
                id: 'id',
                type: 'string',
            },
            {
                id: 'path',
                type: 'string',
            },
            {
                id: 'query',
                type: 'object',
            },
            {
                id: 'params',
                type: 'object',
            },
        ],
    },
];
const getPageInfoFields = () => [
    { id: 'pageId', title: $t('k_b4051f8'), type: 'string', readonly: true },
    { id: 'name', title: $t('k_1e6ffc69'), type: 'string', readonly: true },
    { id: 'title', title: $t('k_d7996547'), type: 'string', readonly: true },
    { id: 'path', title: $t('k_8e33b139'), type: 'string', readonly: true },
];
export const updateGlobalConstantSchema = async (schema) => {
    if (window.w?.mode !== 'editor')
        return;
    if (!window.w?.projectId)
        return;
    const constList = await globalConstantService.list(window.w.projectId);
    const frontConstantList = constList.filter(item => !item.onlyBackend);
    patchWGlobalSchema(schema, frontConstantList);
};
export const patchWGlobalSchema = (schema, constList) => {
    if (schema.id !== 'w' && schema.id !== 'wuji')
        return;
    const fields = schema.fields || [];
    const globalField = fields.find(item => item.id === 'global');
    if (!globalField) {
        fields.push(getGlobalConstantSchema(constList));
        return;
    }
    globalField.fields = getGlobalConstantFields(constList);
};
export const getGlobalConstantFields = (constList) => constList.map((item) => ({
    id: item.constantId,
    title: item.desc,
    ...item.schema,
}));
export const getGlobalConstantSchema = (frontConstantList) => ({
    id: 'global',
    title: $t('k_4c403e'),
    type: 'object',
    readonly: true,
    'uc:collapsed': true,
    fields: getGlobalConstantFields(frontConstantList),
});
const getStaffInternalFields = () => (window.GLOBAL_INFO?.externalDeploy
    ? []
    : [
        { id: 'workDeptId', title: $t('k_6b2526c'), type: 'string', readonly: true, hidden: false },
        { id: 'workDeptIdString', title: $t('k_19d85b52'), type: 'string', readonly: true, hidden: false },
        { id: 'workOrgIdString', title: $t('k_804e17d3'), type: 'string', readonly: true, hidden: false },
        { id: 'positionName', title: $t('k_a38949fb'), type: 'string', readonly: true, hidden: false },
    ]);
const getSSOFields = () => ((SSO_DEPLOY && window.GLOBAL_INFO?.user_sso_frontendData) ? [
    {
        id: 'frontendData',
        title: $t('k_c095a944'),
        type: 'object',
        readonly: true,
        description: $t('k_64fd4991'),
    },
] : []);
const getSwitchAccountMethods = () => (SSO_DEPLOY ? [
    {
        id: 'switchAccount',
        title: $t('k_afde2c92'),
        description: $t('k_3fcb6f75'),
        arguments: [
            { id: 'shallRedirect', type: 'boolean', description: $t('k_f8cbcf73') },
        ],
    },
] : []);
const baseContextDataSchema = {
    fields: [
        { id: 'mode', title: $t('k_1ce4b236'), type: 'string', readonly: true },
        { id: 'env', title: $t('k_fec0a056'), type: 'string', readonly: true },
        {
            id: 'user',
            title: $t('k_5d87d036'),
            type: 'object',
            readonly: true,
            'uc:collapsed': true,
            fields: [
                { id: 'userId', title: $t('k_bef7d5f'), type: 'string', readonly: true },
                { id: 'name', title: $t('k_5e7c1fb9'), type: 'string', readonly: true },
                { id: 'chnName', title: $t('k_7500f155'), type: 'string', readonly: true },
                { id: 'deptName', title: $t('k_85b228c6'), type: 'string', readonly: true },
                { id: 'deptNameString', title: $t('k_5d9522b'), type: 'string', readonly: true },
                { id: 'deptIdString', title: $t('k_b42c7510'), type: 'string', readonly: true },
                { id: 'staffId', title: $t('k_bef7d5f'), type: 'string', readonly: true, hidden: true },
                { id: 'avatar', title: $t('k_8310207'), type: 'string',
                    readonly: true,
                    ui: { type: getAvailableComponent('public', COMP.avatar) } },
                ...getSSOFields(),
                { id: 'editorRole', title: $t('k_cf39d58f'), type: 'string', readonly: true },
                { id: 'isAdmin', title: $t('k_8e1ed2c9'), type: 'boolean', readonly: true },
                { id: 'isLogin', title: $t('k_b71fa3b6'), type: 'boolean', readonly: true },
                ...getStaffInternalFields(),
                {
                    id: 'roles',
                    title: $t('k_749a6a57'),
                    type: 'array',
                    description: $t('k_b94ae843'),
                    readonly: true,
                    items: {
                        type: 'object',
                        readonly: true,
                        fields: [
                            { id: 'role', title: $t('k_1de7d36d'), type: 'string', readonly: true },
                            {
                                id: 'permissions',
                                title: $t('k_aa5c52ce'),
                                type: 'array',
                                readonly: true,
                                items: {
                                    type: 'object',
                                    fields: [
                                        { id: 'permission', title: $t('k_9bf51032'), type: 'string', readonly: true },
                                        { id: 'pageId', title: $t('k_1380578c'), type: 'string', readonly: true },
                                        { id: 'role', title: $t('k_67298115'), type: 'string', readonly: true },
                                    ],
                                },
                            },
                        ],
                    },
                },
                { id: 'orgBgId', title: 'Bg ID', type: 'string', readonly: true, hidden: true },
                { id: 'orgDepartmentId', title: $t('k_6b2526c'), type: 'string', readonly: true, hidden: true },
                { id: 'orgCenterId', title: $t('k_80bfff5'), type: 'string', readonly: true, hidden: true },
                { id: 'orgGroupId', title: $t('k_abbf5d6'), type: 'string', readonly: true, hidden: true },
            ],
            methods: [
                { id: 'logout', title: $t('k_c8eec1a9'), description: $t('k_afd01e56') },
                ...getSwitchAccountMethods(),
            ],
        },
        getGlobalConstantSchema(window.w?.frontConstantList || []),
        {
            id: 'ai',
            title: 'AI',
            tsType: 'import(\'openai\').default',
        },
        {
            id: 'message',
            title: $t('k_37711124'),
            type: 'object',
            readonly: true,
            'uc:bindable': false,
            'uc:collapsed': true,
            methods: [
                {
                    id: 'success',
                    title: $t('k_2e478339'),
                    arguments: [{ id: 'content', type: 'string', description: $t('k_b0104f9d') }],
                },
                {
                    id: 'warning',
                    title: $t('k_8106ac9f'),
                    arguments: [{ id: 'content', type: 'string', description: $t('k_b0104f9d') }],
                },
                {
                    id: 'error',
                    title: $t('k_1b5acfe9'),
                    arguments: [{ id: 'content', type: 'string', description: $t('k_b0104f9d') }],
                },
                {
                    id: 'info',
                    title: $t('k_37711124'),
                    arguments: [{ id: 'content', type: 'string', description: $t('k_b0104f9d') }],
                },
                {
                    id: 'loading',
                    title: $t('k_5d0d5ccd'),
                    arguments: [{ id: 'content', type: 'string', description: $t('k_b0104f9d') }],
                },
            ],
        },
        {
            id: '$app',
            title: $t('k_901ce8d8'),
            type: 'any',
            readonly: true,
            'uc:bindable': false,
            tsType: '$__UICoreContext.AppLessCode',
        },
        {
            id: 'workflow',
            title: $t('k_93c10ead'),
            type: 'object',
            readonly: true,
            'uc:bindable': false,
            'uc:collapsed': true,
            fields: [
                {
                    id: 'relatedEntityList',
                    title: $t('k_5092fb0a'),
                    type: 'array',
                    readonly: true,
                    items: {
                        type: 'object',
                        fields: [
                            { id: '_id', title: $t('k_3ad57267'), type: 'string', readonly: true },
                            { id: 'handlers', title: $t('k_425d0c81'), type: 'string', readonly: true },
                            { id: 'status', title: $t('k_92730769'), type: 'string', readonly: true },
                            { id: 'statusName', title: $t('k_e7115459'), type: 'string', readonly: true },
                            { id: 'flowStatus', title: $t('k_90ac2ee'), type: 'string', readonly: true },
                            { id: 'env', title: $t('k_6ac42065'), type: 'string', readonly: true },
                            { id: 'objectId', title: $t('k_9dafa028'), type: 'string', readonly: true },
                            { id: 'flowId', title: $t('k_de407ccd'), type: 'string', readonly: true },
                        ],
                    },
                },
            ],
            methods: [
                {
                    id: 'fetchEntityList',
                    title: $t('k_44d0bc15'),
                    description: '',
                    arguments: [
                        { id: 'type', type: 'string', description: $t('k_e61b864') },
                        { id: 'filter', type: 'string', description: $t('k_24745bd4') },
                    ],
                },
                {
                    id: 'fetchWorkflowEntity',
                    title: $t('k_e74d6eb8'),
                    description: '',
                    arguments: [
                        { id: 'entityId', type: 'string', description: $t('k_3ad57267') },
                    ],
                },
                {
                    id: 'fetchEntityRecords',
                    title: $t('k_7e4ddb24'),
                    description: '',
                    arguments: [
                        { id: 'entityId', type: 'string', description: $t('k_3ad57267') },
                    ],
                },
                {
                    id: 'start',
                    title: $t('k_6bbb26c1'),
                    description: '',
                    arguments: [
                        { id: 'workflowId', type: 'string', description: $t('k_84f262e2') },
                        { id: 'data', type: 'object', description: $t('k_ca9de38f') },
                    ],
                },
                {
                    id: 'transition',
                    title: $t('k_294155ce'),
                    description: '',
                    arguments: [
                        { id: 'entityId', type: 'string', description: $t('k_3ad57267') },
                        { id: 'data', type: 'object', description: $t('k_ca9de38f') },
                    ],
                },
                {
                    id: 'restart',
                    title: $t('k_182f935e'),
                    description: '',
                    arguments: [
                        { id: 'entityId', type: 'string', description: $t('k_3ad57267') },
                        { id: 'payload', type: 'object', description: $t('k_6faa5926') },
                    ],
                },
                {
                    id: 'abort',
                    title: $t('k_9c38f466'),
                    description: '',
                    arguments: [
                        { id: 'entityId', type: 'string', description: $t('k_3ad57267') },
                    ],
                },
                {
                    id: 'isEntityProcessing',
                    title: $t('k_cd928a8e'),
                    description: '',
                    arguments: [
                        { id: 'entity', type: 'object', description: $t('k_b988962f') },
                    ],
                },
                {
                    id: 'canApproveEntity',
                    title: $t('k_f7f0ad7'),
                    description: '',
                    arguments: [
                        { id: 'entity', type: 'object', description: $t('k_b988962f') },
                    ],
                },
                {
                    id: 'assignEntityHandlers',
                    title: $t('k_b3232aac'),
                    description: '',
                    arguments: [
                        { id: 'entityId', type: 'string', description: $t('k_3ad57267') },
                        { id: 'handlers', type: 'string', description: $t('k_9df2b850') },
                    ],
                },
            ],
        },
        {
            id: 'projectInfo',
            title: $t('k_f73092c'),
            type: 'object',
            readonly: true,
            'uc:bindable': false,
            'uc:collapsed': true,
            fields: [
                { id: 'id', title: $t('k_f4c7d984'), type: 'string', readonly: true },
                { id: 'name', title: $t('k_fa393bc'), type: 'string', readonly: true },
                { id: 'logo', title: $t('k_56b8b199'), type: 'string', readonly: true, ui: { type: getAvailableComponent('public', COMP.image) } },
            ],
        },
        {
            id: 'mainPageInfo',
            title: $t('k_67c40419'),
            type: 'object',
            readonly: true,
            'uc:bindable': false,
            'uc:collapsed': true,
            fields: getPageInfoFields(),
        },
        {
            id: 'pageList',
            title: $t('k_25bcc5f5'),
            type: 'array',
            readonly: true,
            'uc:bindable': false,
            'uc:collapsed': true,
            items: {
                type: 'object',
                fields: getPageInfoFields(),
            },
        },
        {
            id: 'sidebar',
            title: $t('k_338ab8ee'),
            type: 'object',
            readonly: true,
            'uc:bindable': false,
            'uc:collapsed': true,
            methods: [
                {
                    id: 'getCurrentGroup',
                    title: $t('k_77b66f0b'),
                    arguments: [],
                },
                {
                    id: 'setCurrentGroup',
                    title: $t('k_15048c42'),
                    arguments: [
                        { id: 'name', type: 'string', description: $t('k_8802dca4') },
                    ],
                },
                {
                    id: 'setSelectedMenu',
                    title: $t('k_9f906f81'),
                    arguments: [
                        { id: 'names', type: 'array', items: { type: 'string' }, description: $t('k_bb362f85') },
                    ],
                },
                {
                    id: 'setCollapsed',
                    title: $t('k_79de9171'),
                    arguments: [
                        { id: 'collapsed', type: 'boolean', description: $t('k_ff0e1c2f') },
                    ],
                },
                {
                    id: 'getSidebar',
                    title: $t('k_9780da86'),
                    arguments: [],
                },
                {
                    id: 'getSystemSidebar',
                    title: $t('k_7807e7c8'),
                    arguments: [],
                },
                {
                    id: 'setSidebar',
                    title: $t('k_ff8096c2'),
                    arguments: [
                        { id: 'sidebar', type: 'object', description: $t('k_beead710') },
                    ],
                },
                {
                    id: 'getCurrentSidebarNames',
                    title: $t('k_33ee7983'),
                    arguments: [],
                    returnValue: { type: 'array', items: { type: 'string' } },
                },
            ],
        },
        { id: 'host', title: $t('k_b9d153c6'), type: 'string', readonly: true },
        { id: 'path', title: $t('k_5d765ff6'), type: 'string', readonly: true },
        { id: 'query', title: $t('k_48857b7c'), type: 'object', readonly: true },
        { id: 'params', title: $t('k_2bb38f4a'), type: 'object', readonly: true },
        getOrgSchema(),
        getFlowSchema(),
    ],
    methods: [
        // 确认框
        {
            id: 'confirm',
            title: $t('k_39551160'),
            arguments: [
                {
                    id: 'option',
                    type: 'object',
                    description: $t('k_20c3663b'),
                    fields: [
                        { id: 'title', type: 'string', required: true, description: $t('k_293e7ce5') },
                        { id: 'content', type: 'string', description: $t('k_accc868') },
                        { id: 'okText', type: 'string', description: $t('k_40f69562') },
                        { id: 'cancelText', type: 'string', description: $t('k_d746b537') },
                        { id: 'onOk', type: 'any', description: $t('k_8164a120') },
                        { id: 'onCancel', type: 'any', description: $t('k_95eee7a9') },
                    ],
                },
            ],
        },
        {
            id: 'showGlobalModal',
            title: $t('k_489b7125'),
            description: $t('k_1c4ee350'),
            isAsync: true,
            arguments: [
                { id: 'id', type: 'string', required: true, description: $t('k_f9df9c') },
                { id: 'data', type: 'object', description: $t('k_c9b86b9f') },
                { id: 'opt', type: 'object', description: $t('k_7dc3e87e') },
            ],
        },
        {
            id: 'hideGlobalModal',
            title: $t('k_2fa4e109'),
            description: $t('k_b81bddf8'),
            arguments: [
                { id: 'id', type: 'string', description: $t('k_8d94ad51') },
                { id: 'data', type: 'object', description: $t('k_a3efaa1f') },
                { id: 'opt', type: 'object', description: $t('k_866e07f7') },
            ],
        },
        // 云函数
        {
            id: 'callFunc',
            title: $t('k_ecfdad6e'),
            isAsync: true,
            arguments: [
                { id: 'funcName', type: 'string', required: true, description: $t('k_ed3d16ac') },
                { id: 'axiosOptions', type: 'object', description: $t('k_18bc59c2'), tsType: 'import("axios").AxiosRequestConfig' },
            ],
        },
        // 公共参数
        {
            id: 'getCommonQueryValue',
            title: $t('k_179b3b81'),
            arguments: [
                { id: 'key', type: 'string', required: true, description: $t('k_f7f034fd') },
            ],
        },
        {
            id: 'getCommonQueryOptions',
            title: $t('k_634e9504'),
            arguments: [
                { id: 'key', type: 'string', required: true, description: $t('k_f7f034fd') },
            ],
        },
        {
            id: 'fetchCommonQueryOptions',
            title: $t('k_f517814e'),
            isAsync: true,
            description: $t('k_af9a1d3b'),
            arguments: [
                { id: 'key', type: 'string', required: true, description: $t('k_f7f034fd') },
            ],
        },
        // 权限
        {
            id: 'hasPermission',
            title: $t('k_385deb37'),
            description: $t('k_30435cfb'),
            arguments: [
                { id: 'permission', type: 'string', required: true, description: $t('k_df46af87') },
                // 可选 pageId 参数，不用直接补全，因而注释
                // { id: 'pageId', type: 'string', description: '可选，指定需要校验的页面 id' },
            ],
        },
        {
            id: 'isRoleMemberOf',
            title: $t('k_3ed94e02'),
            description: $t('k_712d3e6a'),
            arguments: [
                { id: 'role', type: 'string', required: true, description: $t('k_a0d527c3') },
            ],
        },
        // 路由
        {
            id: 'navigateTo',
            title: $t('k_ad5d6da'),
            description: $t('k_d59cac02'),
            arguments: getNavigateArguments(),
        },
        {
            id: 'redirectTo',
            title: $t('k_ad5d6da'),
            description: $t('k_fdfafdf3'),
            arguments: getNavigateArguments(),
        },
        {
            id: 'openPage',
            title: $t('k_f6561836'),
            description: $t('k_7ffd6bb3'),
            arguments: getNavigateArguments(),
        },
        {
            id: 'getResolveHref',
            title: $t('k_d70a0368'),
            arguments: getNavigateArguments(),
            returnValue: { type: 'string' },
        },
        // 滚动
        {
            id: 'scrollTo',
            title: $t('k_cc480ccb'),
            arguments: [
                {
                    id: 'option',
                    type: 'object',
                    description: $t('k_7b0316b3'),
                    fields: [
                        { id: 'top', type: 'number', default: 0 },
                        { id: 'behavior', type: 'string', enum: [{ value: 'instant' }, { value: 'smooth' }] },
                    ],
                },
            ],
        },
        // 上报
        {
            id: 'report',
            title: $t('k_37b003e2'),
            arguments: [
                {
                    id: 'data',
                    type: 'object',
                    required: true,
                    description: $t('k_676ffdd'),
                    fields: [
                        { id: 'event', type: 'string', required: true, description: $t('k_ebeb0203') },
                        { id: 'eid', type: 'string', required: true, description: $t('k_824ce41f') },
                    ],
                },
            ],
        },
    ],
};
const commonContextDataSchema = {
    fields: [
        getUtilsSchema(),
        {
            id: 'atoms',
            title: $t('k_496f9183'),
            type: 'object',
            readonly: true,
            'uc:bindable': false,
            'uc:collapsed': true,
            methods: [
                {
                    id: 'alert',
                    title: $t('k_f94021f4'),
                    arguments: [
                        {
                            id: 'params',
                            type: 'object',
                            required: true,
                            fields: [
                                { id: 'text', type: 'string', required: true, description: $t('k_389afe34') },
                                { id: 'icon', type: 'string', enum: [{ value: '' }, { value: 'info' }, { value: 'success' }, { value: 'error' }, { value: 'success' }] },
                            ],
                        },
                    ],
                },
                {
                    id: 'confirm',
                    title: $t('k_6c21d053'),
                    isAsync: true,
                    arguments: [
                        {
                            id: 'params',
                            type: 'object',
                            required: true,
                            fields: [
                                { id: 'text', type: 'string', required: true, description: $t('k_389afe34') },
                                { id: 'centered', type: 'boolean', description: $t('k_180ccf54') },
                                { id: 'icon', type: 'string', description: $t('k_c72881c6') },
                                { id: 'okText', type: 'string', description: $t('k_40f69562') },
                                { id: 'cancelText', type: 'string', description: $t('k_d746b537') },
                                { id: 'onOk', type: 'any', description: $t('k_f643e952') },
                                { id: 'onCancel', type: 'any', description: $t('k_27835c43') },
                            ],
                        },
                    ],
                    returnValue: { type: 'boolean' },
                },
                {
                    id: 'prompt',
                    title: $t('k_c3e01b2f'),
                    isAsync: true,
                    arguments: [
                        {
                            id: 'params',
                            type: 'object',
                            required: true,
                            fields: [
                                { id: 'message', type: 'string', required: true, description: $t('k_389afe34') },
                                { id: 'defaultValue', type: 'string', description: $t('k_87b97ef6') },
                                {
                                    id: 'inputType',
                                    type: 'string',
                                    description: $t('k_34f51639'),
                                    enum: [{ value: 'input' }, { value: 'textarea' }, { value: 'password' }],
                                },
                                { id: 'allowEmpty', type: 'boolean', description: $t('k_2b922dc5') },
                            ],
                        },
                    ],
                },
                {
                    id: 'message',
                    title: $t('k_4be19654'),
                    arguments: [
                        {
                            id: 'params',
                            type: 'object',
                            required: true,
                            fields: [
                                { id: 'text', type: 'string', required: true, description: $t('k_389afe34') },
                                { id: 'type', type: 'string', enum: [{ value: 'success' }, { value: 'error' }, { value: 'info' }, { value: 'loading' }, { value: 'warn' }] },
                                { id: 'duration', type: 'number', description: $t('k_fb99065f') },
                            ],
                        },
                    ],
                },
                {
                    id: 'notification',
                    title: $t('k_6d00667a'),
                    isAsync: true,
                    arguments: [
                        {
                            id: 'params',
                            type: 'object',
                            required: true,
                            fields: [
                                { id: 'message', type: 'string', required: true, description: $t('k_389afe34') },
                                { id: 'description', type: 'string', required: true, description: $t('k_6d2a8848') },
                                { id: 'type', type: 'string', description: $t('k_9cd43e7e'), enum: [{ value: 'open' }, { value: 'error' }, { value: 'success' }, { value: 'warn' }] },
                                { id: 'placement', type: 'string', description: $t('k_e5235e35'), enum: [{ value: 'topLeft' }, { value: 'topRight' }, { value: 'bottomLeft' }, { value: 'bottomRight' }] },
                                { id: 'duration', type: 'number', description: $t('k_fb99065f') },
                            ],
                        },
                    ],
                },
                {
                    id: 'validateForm',
                    title: $t('k_9adebda7'),
                    isAsync: true,
                    arguments: [
                        {
                            id: 'params',
                            type: 'object',
                            required: true,
                            fields: [
                                { id: 'target', type: 'string', required: true, description: $t('k_4c9a311f') },
                                { id: 'errorMessage', type: 'string', description: $t('k_92596fa8') },
                            ],
                        },
                    ],
                    returnValue: { type: 'boolean' },
                },
                {
                    id: 'invokeLessCode',
                    title: $t('k_a11a741b'),
                    isAsync: true,
                    arguments: [
                        { id: 'methodName', type: 'string', required: true, description: $t('k_e2212c0c') },
                    ],
                },
                {
                    id: 'clipboardWriteText',
                    title: $t('k_bd62d0a0'),
                    isAsync: true,
                    arguments: [
                        { id: 'text', type: 'string', required: true, description: $t('k_81daa3b0') },
                        {
                            id: 'opt',
                            type: 'object',
                            fields: [
                                { id: 'successMessage', type: 'boolean', description: $t('k_5fcf4d2d') },
                            ],
                        },
                    ],
                },
                {
                    id: 'invokeDataSourceMethod',
                    title: $t('k_85be1567'),
                    isAsync: true,
                    arguments: [
                        {
                            id: 'params',
                            type: 'object',
                            required: true,
                            fields: [
                                { id: 'id', type: 'string', required: true, description: $t('k_6e87ec70') },
                                { id: 'method', type: 'string', required: true, description: $t('k_baaf2048') },
                                { id: 'args', type: 'any', description: $t('k_59c09a5a') },
                            ],
                        },
                    ],
                },
                {
                    id: 'dataSourceReInit',
                    title: $t('k_557e6824'),
                    isAsync: true,
                },
                {
                    id: 'uploadToCOS',
                    title: $t('k_3c39e538'),
                    isAsync: true,
                    arguments: [
                        {
                            id: 'params',
                            type: 'object',
                            required: true,
                            fields: [
                                { id: 'cosId', type: 'string', required: true, description: $t('k_cd081a4c') },
                                { id: 'file', type: 'string', required: true, description: $t('k_de0484e8') },
                                { id: 'path', type: 'string', description: $t('k_e839028a') },
                                { id: 'envConfig', type: 'object', description: $t('k_b3272cb3') },
                                { id: 'headers', type: 'object', description: $t('k_a691eb9') },
                            ],
                        },
                    ],
                    returnValue: { type: 'string', description: $t('k_23373bc5') },
                },
                {
                    id: 'exportExcel',
                    title: $t('k_6809e9d5'),
                    isAsync: true,
                    arguments: [
                        {
                            id: 'params',
                            type: 'object',
                            required: true,
                            fields: [
                                { id: 'data', type: 'array', required: true, description: $t('k_50b570bc') },
                                {
                                    id: 'fields',
                                    type: 'array',
                                    required: true,
                                    description: $t('k_350ec636'),
                                    items: {
                                        type: 'object',
                                        fields: [
                                            { id: 'dataKey', type: 'string', required: true, description: $t('k_557187b6') },
                                            { id: 'colName', type: 'string', required: true, description: $t('k_e30c871a') },
                                        ],
                                    },
                                },
                                { id: 'fileName', type: 'string', required: true, description: $t('k_760abed2') },
                                { id: 'sheetName', type: 'string', required: true, description: $t('k_42114a9f') },
                                { id: 'format', type: 'string', description: $t('k_b5de97d2'), enum: [{ value: 'xlsx' }, { value: 'xlsm' }] },
                            ],
                        },
                    ],
                },
                {
                    id: 'importExcel',
                    title: $t('k_1f3cd2f4'),
                    isAsync: true,
                    arguments: [
                        {
                            id: 'params',
                            type: 'object',
                            required: true,
                            description: $t('k_a1b1698d'),
                            fields: [
                                { id: 'raw', type: 'boolean', description: $t('k_5cfef3e1') },
                                { id: 'cellDates', type: 'boolean', description: $t('k_cc449b3d') },
                                { id: 'codepage', type: 'string', description: $t('k_8f182987') },
                            ],
                        },
                    ],
                    returnValue: { type: 'object' },
                },
                {
                    id: 'getFileData',
                    title: $t('k_5c776d11'),
                    isAsync: true,
                    arguments: [
                        {
                            id: 'params',
                            type: 'object',
                            fields: [
                                { id: 'multiple', type: 'boolean', description: $t('k_1a587cd5') },
                                { id: 'enableDirectory', type: 'boolean', description: $t('k_a630d32e') },
                                { id: 'codepage', type: 'string', description: $t('k_8f182987') },
                            ],
                        },
                    ],
                },
                {
                    id: 'openLink',
                    title: $t('k_a2c301ef'),
                    isAsync: true,
                    arguments: [
                        { id: 'link', type: 'string', required: true, description: $t('k_812867e') },
                        { id: 'newWindow', type: 'boolean', description: $t('k_26da58db') },
                    ],
                },
                {
                    id: 'historyBack',
                    title: $t('k_2490874'),
                },
                {
                    id: 'refreshPage',
                    title: $t('k_3ac2cf99'),
                },
                {
                    id: 'reloadRenderer',
                    title: $t('k_ff216fbf'),
                },
                {
                    id: 'pageScroll',
                    title: $t('k_9b3199e4'),
                    arguments: [
                        {
                            id: 'params',
                            type: 'object',
                            fields: [
                                { id: 'position', type: 'string', required: true, description: $t('k_5658bf6e'), enum: [{ value: 'top' }, { value: 'bottom' }, { value: 'y' }, { value: 'component' }] },
                                { id: 'y', type: 'number', description: $t('k_28098687') },
                                { id: 'componentId', type: 'string', description: $t('k_dbe68ac8') },
                                { id: 'componentOffsetType', type: 'string', description: $t('k_5b429f8b'), enum: [{ value: 'start' }, { value: 'end' }] },
                                { id: 'componentOffset', type: 'number', description: $t('k_f8b051f9') },
                                { id: 'behavior', type: 'string', description: $t('k_49f0e26'), enum: [{ value: 'instant' }, { value: 'smooth' }] },
                            ],
                        },
                    ],
                },
                {
                    id: 'saveDraft',
                    title: $t('k_29510c35'),
                    isAsync: true,
                    arguments: [
                        {
                            id: 'params',
                            type: 'object',
                            fields: [
                                { id: 'namespace', type: 'string', required: true, description: $t('k_8ee067a1') },
                                { id: 'showConfirm', type: 'boolean', description: $t('k_714bd626') },
                            ],
                        },
                    ],
                },
                {
                    id: 'restoreDraft',
                    title: $t('k_202752ab'),
                    isAsync: true,
                    arguments: [
                        {
                            id: 'params',
                            type: 'object',
                            fields: [
                                { id: 'namespace', type: 'string', required: true, description: $t('k_8ee067a1') },
                                { id: 'showConfirm', type: 'boolean', description: $t('k_714bd626') },
                                { id: 'remainDraftAfterCancel', type: 'boolean', description: $t('k_5fce6fb0') },
                            ],
                        },
                    ],
                },
                {
                    id: 'removeDraft',
                    title: $t('k_895cadaf'),
                    isAsync: true,
                    arguments: [
                        {
                            id: 'params',
                            type: 'object',
                            fields: [
                                { id: 'namespace', type: 'string', required: true, description: $t('k_8ee067a1') },
                            ],
                        },
                    ],
                },
                {
                    id: 'email',
                    title: $t('k_c5052b21'),
                    isAsync: true,
                    arguments: [
                        {
                            id: 'params',
                            type: 'object',
                            fields: [
                                { id: 'to', type: 'string', required: true, description: $t('k_3a3856d5') },
                                { id: 'cc', type: 'string', description: $t('k_7ba688d4') },
                                { id: 'bcc', type: 'string', description: $t('k_e996f2ec') },
                                { id: 'title', type: 'string', required: true, description: $t('k_80755700') },
                                { id: 'content', type: 'string', required: true, description: $t('k_d8bbcc26') },
                            ],
                        },
                    ],
                },
                {
                    id: 'wxworkRobot',
                    title: $t('k_6a73fffe'),
                    isAsync: true,
                    arguments: [
                        {
                            id: 'params',
                            type: 'object',
                            fields: [
                                { id: 'robotId', type: 'string', required: true, description: $t('k_78442bab') },
                                { id: 'content', type: 'string', required: true, description: $t('k_d4c387bd') },
                                { id: 'successMessage', type: 'boolean', required: true, description: $t('k_871a2b73') },
                            ],
                        },
                    ],
                },
                {
                    id: 'wxworkNotification',
                    title: $t('k_76deb9d2'),
                    isAsync: true,
                    arguments: [
                        {
                            id: 'params',
                            type: 'object',
                            fields: [
                                { id: 'app', type: 'string', required: true, description: $t('k_f4c7d984') },
                                { id: 'receivers', type: 'string', required: true, description: $t('k_26dfe8b8') },
                                { id: 'type', type: 'string', required: true, description: $t('k_9cd43e7e'), enum: [{ value: 'text' }, { value: 'textcard' }, { value: 'markdown' }] },
                                { id: 'content', type: 'string', description: $t('k_d4c387bd') },
                                { id: 'title', type: 'string', description: $t('k_9dcb759a') },
                                { id: 'description', type: 'string', description: $t('k_c578bff5') },
                                { id: 'url', type: 'string', description: $t('k_91d67a50') },
                                { id: 'buttonText', type: 'string', description: $t('k_99a738b7') },
                            ],
                        },
                    ],
                },
                {
                    id: 'openWxwork',
                    title: $t('k_6ebe5600'),
                    isAsync: true,
                    arguments: [
                        {
                            id: 'params',
                            type: 'object',
                            fields: [
                                { id: 'type', type: 'string', required: true, enum: [{ value: 'username' }, { value: 'uin' }] },
                                { id: 'value', type: 'string', required: true },
                            ],
                        },
                    ],
                },
                {
                    id: 'sendGptMessage',
                    title: $t('k_ecf43a43'),
                    isAsync: true,
                    arguments: [
                        {
                            id: 'params',
                            type: 'object',
                            fields: [
                                { id: 'systemContent', type: 'string', required: true, description: $t('k_550f765b') },
                                { id: 'message', type: 'string', required: true, description: $t('k_f81c9678') },
                                { id: 'type', type: 'string', enum: [{ value: 'sync' }, { value: 'flow' }], description: $t('k_2da7ea70') },
                                {
                                    id: 'flowSetting',
                                    type: 'object',
                                    fields: [
                                        { id: 'flowType', type: 'string', required: true, enum: [{ value: 'joinString' }, { value: 'callMethod' }] },
                                        { id: 'jsonString', type: 'string', description: $t('k_1013fb89') },
                                        { id: 'callMethod', type: 'string', description: $t('k_85bf7666') },
                                    ],
                                },
                                { id: 'model', type: 'string', description: $t('k_976bc490') },
                            ],
                        },
                    ],
                },
            ],
        },
        {
            id: 'mainRenderer',
            title: $t('k_bb65fe69'),
            type: 'any',
            tsType: 'Vue',
            readonly: true,
            'uc:bindable': false,
        },
        {
            id: 'filter',
            title: $t('k_dd04ffae'),
            type: 'object',
            readonly: true,
            methods: [
                {
                    id: 'toFilterArray',
                    title: $t('k_432bf6f0'),
                    description: '',
                    arguments: [
                        { id: 'filter', type: 'object', description: $t('k_e67e4b23') },
                        { id: 'options', type: 'object', description: $t('k_473a7ed6') },
                    ],
                },
                {
                    id: 'toFilterExpr',
                    title: $t('k_82cefc2e'),
                    description: '',
                    arguments: [
                        { id: 'filter', type: 'object', description: $t('k_e67e4b23') },
                        { id: 'type', type: 'string', description: $t('k_8fc3cc81') },
                        { id: 'options', type: 'object', description: $t('k_473a7ed6') },
                    ],
                },
                {
                    id: 'toNestedFilterExpr',
                    title: $t('k_af2f5511'),
                    description: '',
                    arguments: [
                        { id: 'filter', type: 'object', description: $t('k_e67e4b23') },
                    ],
                },
            ],
        },
        {
            id: 'emitter',
            title: $t('k_d72c9a1'),
            type: 'object',
            readonly: true,
            'uc:bindable': false,
            'uc:collapsed': true,
            fields: [
                {
                    id: 'EventName',
                    title: $t('k_5c8b673a'),
                    type: 'object',
                    readonly: true,
                    'uc:bindable': false,
                    'uc:collapsed': true,
                    fields: [
                        { id: 'ROUTER_BEFORE_EACH', title: $t('k_2ae35487'), type: 'string' },
                        { id: 'ROUTER_AFTER_EACH', title: $t('k_5769cb98'), type: 'string' },
                        { id: 'PAGE_BEFORE_DESTROY', title: $t('k_b35037a0'), type: 'string' },
                        { id: 'PAGE_DESTROYED', title: $t('k_93af025f'), type: 'string' },
                        { id: 'PAGE_DATASOURCE_READY', title: $t('k_6f4c4660'), type: 'string' },
                        { id: 'PAGE_TITLE_CHANGE', title: $t('k_df4be9a1'), type: 'string' },
                    ],
                },
            ],
            methods: [
                {
                    id: 'on',
                    title: $t('k_282d5f6d'),
                    description: '',
                    arguments: [
                        { id: 'eventName', type: 'string', description: $t('k_d709f01c'), required: true },
                        { id: 'fn', type: 'any', description: $t('k_eadb18cc'), required: true },
                    ],
                },
                {
                    id: 'once',
                    title: $t('k_71716b0'),
                    description: '',
                    arguments: [
                        { id: 'eventName', type: 'string', description: $t('k_d709f01c'), required: true },
                        { id: 'fn', type: 'any', description: $t('k_eadb18cc'), required: true },
                    ],
                },
                {
                    id: 'off',
                    title: $t('k_cc60abc1'),
                    description: '',
                    arguments: [
                        { id: 'eventName', type: 'string', description: $t('k_d709f01c'), required: true },
                        { id: 'fn', type: 'any', description: $t('k_eadb18cc') },
                    ],
                },
                {
                    id: 'emit',
                    title: $t('k_949dce81'),
                    description: '',
                    arguments: [
                        { id: 'eventName', type: 'string', description: $t('k_d709f01c'), required: true },
                        { id: 'args', type: 'any', description: $t('k_35a1ec73') },
                    ],
                },
            ],
        },
        {
            id: 'permission',
            title: $t('k_d4725bd1'),
            type: 'object',
            readonly: true,
            'uc:bindable': false,
            'uc:collapsed': true,
            methods: [
                {
                    id: 'fetchRoleList',
                    title: $t('k_26abe818'),
                    description: '',
                    arguments: [],
                },
                {
                    id: 'fetchRoleUserList',
                    title: $t('k_e879fbcb'),
                    description: '',
                    arguments: [
                        { id: 'bizId', type: 'string', description: $t('k_cb0e8b54'), default: '' },
                        { id: 'roleId', type: 'string', description: $t('k_651210a5'), default: '' },
                        { id: 'opt', type: 'object', description: $t('k_cba0e07e'), default: { id: '', type: '' } },
                    ],
                },
                {
                    id: 'fetchRolePermissionList',
                    title: $t('k_a74c2567'),
                    description: '',
                    arguments: [
                        { id: 'roleId', type: 'string', description: $t('k_5af39aee') },
                    ],
                },
                {
                    id: 'fetchRoleView',
                    title: $t('k_bb84828c'),
                    description: '',
                    arguments: [
                        { id: 'roleId', type: 'string', description: $t('k_5af39aee') },
                    ],
                },
                {
                    id: 'fetchPermissionList',
                    title: $t('k_fc86b69d'),
                    description: '',
                    arguments: [],
                },
                {
                    id: 'fetchBuiltinPermissionList',
                    title: $t('k_9e4afe20'),
                    description: '',
                    arguments: [],
                },
                {
                    id: 'fetchApiPermissionList',
                    title: $t('k_1bb1b1f9'),
                    description: '',
                    arguments: [],
                },
                {
                    id: 'createRole',
                    title: $t('k_d4f0b53b'),
                    description: '',
                    arguments: [
                        {
                            id: 'data',
                            title: $t('k_1b18e8e9'),
                            type: 'object',
                            fields: [
                                { id: 'name', type: 'string', description: $t('k_f9b55d97') },
                                { id: 'cname', type: 'string', description: $t('k_ae615ff9') },
                                { id: 'type', enum: [{ value: '' }, { value: 'biz' }], description: $t('k_8bb73dbc') },
                            ],
                        },
                    ],
                },
                {
                    id: 'updateRole',
                    title: $t('k_7488231a'),
                    description: '',
                    arguments: [
                        {
                            id: 'data',
                            title: $t('k_1b18e8e9'),
                            description: $t('k_8cf55902'),
                            type: 'object',
                            fields: [
                                { id: 'name', type: 'string', description: $t('k_f9b55d97') },
                                { id: 'cname', type: 'string', description: $t('k_ae615ff9') },
                                { id: 'type', enum: [{ value: '' }, { value: 'biz' }], description: $t('k_8bb73dbc') },
                            ],
                        },
                    ],
                },
                {
                    id: 'deleteRole',
                    title: $t('k_97bda7ed'),
                    description: '',
                    arguments: [
                        { id: 'name', type: 'string', description: $t('k_f9b55d97') },
                    ],
                },
                {
                    id: 'createPermission',
                    title: $t('k_a55875dd'),
                    description: '',
                    arguments: [
                        {
                            id: 'data',
                            title: $t('k_2c97ec6a'),
                            type: 'object',
                            fields: [
                                { id: 'name', type: 'string', description: $t('k_f2e2e311') },
                                { id: 'cname', type: 'string', description: $t('k_b54458d1') },
                                { id: 'type', enum: [{ value: 'page' }, { value: 'api' }, { value: 'set' }], description: $t('k_8fc3cc81') },
                                { id: 'relatedPages', type: 'string', description: $t('k_318dce62') },
                            ],
                        },
                    ],
                },
                {
                    id: 'updatePermission',
                    title: $t('k_8d54bf52'),
                    description: '',
                    arguments: [
                        {
                            id: 'data',
                            title: $t('k_2c97ec6a'),
                            description: $t('k_69dbd344'),
                            type: 'object',
                            fields: [
                                { id: 'name', type: 'string', description: $t('k_f2e2e311') },
                                { id: 'cname', type: 'string', description: $t('k_b54458d1') },
                                { id: 'type', enum: [{ value: 'page' }, { value: 'api' }, { value: 'set' }], description: $t('k_8fc3cc81') },
                                { id: 'relatedPages', type: 'string', description: $t('k_318dce62') },
                            ],
                        },
                    ],
                },
                {
                    id: 'deletePermission',
                    title: $t('k_29da5cbf'),
                    description: '',
                    arguments: [
                        { id: 'name', type: 'string', description: $t('k_f2e2e311') },
                    ],
                },
                {
                    id: 'createRoleUser',
                    title: $t('k_4e7e068b'),
                    description: '',
                    arguments: [
                        {
                            id: 'data',
                            title: $t('k_c4149bf5'),
                            type: 'object',
                            fields: [
                                { id: 'role', type: 'string', description: $t('k_f9b55d97') },
                                { id: 'userId', type: 'string', description: $t('k_14314ec0') },
                                { id: 'userName', type: 'string', description: $t('k_dee6b505') },
                                { id: 'userType', enum: [{ value: 'staff' }, { value: 'org' }], description: $t('k_2c818d6') },
                                { id: 'bizId', type: 'string', description: $t('k_f65edbbd') },
                                { id: 'expireAt', type: 'number', description: $t('k_76762b9c') },
                            ],
                        },
                    ],
                },
                {
                    id: 'deleteRoleUser',
                    title: $t('k_9e9a70e2'),
                    description: '',
                    arguments: [
                        { id: 'id', type: 'string', description: $t('k_8617382') },
                    ],
                },
                {
                    id: 'createRolePermission',
                    title: $t('k_177f55ae'),
                    description: '',
                    arguments: [
                        {
                            id: 'data',
                            title: $t('k_a72d6c1d'),
                            type: 'object',
                            fields: [
                                { id: 'role', type: 'string', description: $t('k_f9b55d97') },
                                { id: 'permission', type: 'string', description: $t('k_f2e2e311') },
                                { id: 'pageId', type: 'string', description: $t('k_db13d6d4') },
                            ],
                        },
                    ],
                },
                {
                    id: 'deleteRolePermission',
                    title: $t('k_3c1557fd'),
                    description: '',
                    arguments: [
                        { id: 'id', type: 'string', description: $t('k_5b6015f2') },
                    ],
                },
                {
                    id: 'batchDeleteRolePermission',
                    title: $t('k_d8c8d661'),
                    description: '',
                    arguments: [
                        { id: 'ids', type: 'array', description: $t('k_b294b93f'), items: { type: 'string' } },
                    ],
                },
                {
                    id: 'createApiPermission',
                    title: $t('k_d1f26184'),
                    description: '',
                    arguments: [
                        {
                            id: 'data',
                            title: $t('k_3345f8e2'),
                            type: 'object',
                            fields: [
                                { id: 'name', type: 'string', description: $t('k_da3197fa') },
                                { id: 'permission', type: 'string', description: $t('k_f2e2e311') },
                                { id: 'api', type: 'string', description: '' },
                                { id: 'method', type: 'string', description: '' },
                                { id: 'type', enum: [{ value: 'collection-cgi' }, { value: 'cloud-function' }], description: '' },
                            ],
                        },
                    ],
                },
                {
                    id: 'deleteApiPermission',
                    title: $t('k_25a152be'),
                    description: '',
                    arguments: [
                        { id: 'id', type: 'string', description: $t('k_32b0e6c5') },
                    ],
                },
            ],
        },
        // 获取运行时公告
        {
            id: 'announcement',
            title: $t('k_3fed3f2a'),
            type: 'object',
            readonly: true,
            methods: [
                {
                    id: 'getList',
                    title: $t('k_3964eb77'),
                    description: '',
                    arguments: [
                        { id: 'filter', type: 'string', description: $t('k_e67e4b23') },
                    ],
                },
                {
                    id: 'refresh',
                    title: $t('k_121a2081'),
                    description: '',
                },
            ],
        },
        {
            id: 'watermark',
            title: $t('k_850ced59'),
            type: 'object',
            readonly: true,
            methods: [
                {
                    id: 'show',
                    title: $t('k_3fef3aec'),
                },
            ],
        },
        { id: 'isMiniProgram', title: $t('k_cc53a57a'), type: 'boolean', readonly: true },
        { id: 'isNarrowScreen', title: $t('k_c58f67ec'), type: 'boolean', readonly: true },
        { id: 'screen', title: $t('k_73226afd'), type: 'object', readonly: true, 'uc:bindable': false },
    ],
    methods: [
        {
            id: 'getAtoms',
            title: $t('k_4c6cedcc'),
            arguments: [
                { id: 'renderer', type: 'any', required: true, description: $t('k_f4804b8b') },
            ],
        },
        {
            id: 'getComponentById',
            title: $t('k_25e2291e'),
            arguments: [
                { id: 'id', type: 'string', required: true, description: $t('k_75206eff') },
            ],
            isAsync: true,
            returnValue: {
                type: 'any',
                tsType: 'Vue',
                readonly: true,
            },
        },
        {
            id: 'getRenderByCompInstance',
            title: $t('k_c2355592'),
            arguments: [
                { id: 'comp', type: 'any', tsType: 'Vue', required: true, description: $t('k_1d4f7fac') },
            ],
        },
        {
            id: 'waitForRendererMounted',
            title: $t('k_a8b6fae2'),
            description: $t('k_a1296606'),
            isAsync: true,
            arguments: [],
        },
        {
            id: 'waitForDataSourceReady',
            title: $t('k_758587d2'),
            description: $t('k_1013a9c9'),
            isAsync: true,
            arguments: [],
        },
        // HTTP 请求
        {
            id: 'request',
            title: $t('k_7b38c09a'),
            description: $t('k_105269e2'),
            arguments: [
                { id: 'url', type: 'string', required: true, description: $t('k_f72363bb') },
                { id: 'option', type: 'object', description: $t('k_cbea7ea4') },
            ],
        },
        // 引入模块
        {
            id: 'require',
            title: $t('k_698205f2'),
            arguments: [
                { id: 'path', type: 'string', required: true, description: $t('k_66af362d') },
            ],
        },
        // 数据源
        {
            id: 'requestCgi',
            title: $t('k_dac74fd4'),
            isAsync: true,
            arguments: [
                { id: 'collectionId', type: 'string', required: true, description: $t('k_45f01792') },
                { id: 'cgiId', type: 'string', required: true, description: $t('k_a10bbcc') },
                {
                    id: 'params',
                    type: 'object',
                    description: $t('k_c342f7d1'),
                    fields: [
                        {
                            id: 'query',
                            type: 'object',
                            description: $t('k_eacb5a52'),
                        },
                        {
                            id: 'body',
                            type: 'object',
                            description: $t('k_150993ae'),
                        },
                        {
                            id: 'headers',
                            type: 'object',
                            description: $t('k_77be7d06'),
                        },
                        {
                            id: 'params',
                            type: 'object',
                            description: $t('k_1ce66f0f'),
                        },
                    ],
                },
            ],
        },
    ],
};
const pageletContextDataSchema = {
    fields: [
        {
            id: 'renderer',
            title: $t('k_18c54511'),
            type: 'any',
            tsType: 'Vue',
            readonly: true,
            'uc:bindable': false,
        },
        {
            id: 'pageInfo',
            title: $t('k_a119f596'),
            type: 'object',
            readonly: true,
            'uc:bindable': false,
            'uc:collapsed': true,
            fields: getPageInfoFields(),
        },
    ],
    methods: [
        // 弹窗
        {
            id: 'showModal',
            title: $t('k_b2d9b5a4'),
            description: $t('k_1b18feb6'),
            isAsync: true,
            arguments: [
                { id: 'id', type: 'string', required: true, description: $t('k_9d4adcb') },
                { id: 'data', type: 'object', description: $t('k_7fbf2423') },
            ],
        },
        {
            id: 'hideModal',
            title: $t('k_87bb5e80'),
            description: $t('k_6b86856d'),
            arguments: [
                { id: 'id', type: 'string', description: $t('k_e40a39c8') },
                { id: 'data', type: 'object', description: $t('k_4a9f930d') },
            ],
        },
    ],
};
const aiPageContextDataSchema = {
    fields: [],
    methods: [],
};
function getFitDataSchema(dataSchema) {
    return Object.freeze(fitSchemaFieldUIType(dataSchema));
}
export function getCommonContextDataSchema() {
    return getFitDataSchema(cloneDeep({
        id: 'w',
        title: $t('k_a6701c4b'),
        type: 'object',
        readonly: true,
        'uc:collapsed': true,
        'uc:bindable': false,
        fields: [
            ...(baseContextDataSchema?.fields ?? []),
            ...(commonContextDataSchema?.fields ?? []),
        ],
        methods: [
            ...(baseContextDataSchema?.methods ?? []),
            ...(commonContextDataSchema?.methods ?? []),
        ],
    }));
}
export function getPageletContextDataSchema() {
    return getFitDataSchema(cloneDeep({
        id: 'w',
        title: $t('k_a6701c4b'),
        type: 'object',
        readonly: true,
        'uc:collapsed': true,
        'uc:bindable': false,
        fields: [
            ...(baseContextDataSchema?.fields ?? []),
            ...(commonContextDataSchema?.fields ?? []),
            ...(pageletContextDataSchema?.fields ?? []),
        ],
        methods: [
            ...(baseContextDataSchema?.methods ?? []),
            ...(commonContextDataSchema?.methods ?? []),
            ...(pageletContextDataSchema?.methods ?? []),
        ],
    }));
}
export function getAIPageContextDataSchema() {
    let baseFields = cloneDeep(baseContextDataSchema?.fields ?? []);
    const baseMethods = cloneDeep(baseContextDataSchema?.methods ?? []);
    // 移除 projectInfo, pageInfo, mainPageInfo, pageList 字段
    baseFields = baseFields.filter(field => !['projectInfo', 'pageInfo', 'mainPageInfo', 'pageList'].includes(field.id));
    // 移除 sidebar 除了 setCollapsed 方法外的其他方法
    const sidebar = baseFields.find(field => field.id === 'sidebar');
    if (sidebar?.methods?.length) {
        sidebar.methods = sidebar.methods.filter(method => ['setCollapsed'].includes(method.id));
    }
    const aiPageFields = cloneDeep(aiPageContextDataSchema?.fields ?? []);
    const aiPageMethods = cloneDeep(aiPageContextDataSchema?.methods ?? []);
    return getFitDataSchema(cloneDeep({
        id: 'wuji',
        title: $t('k_67415b6'),
        type: 'object',
        readonly: true,
        fields: [
            ...baseFields,
            ...aiPageFields,
        ],
        methods: [
            ...baseMethods,
            ...aiPageMethods,
        ],
    }));
}
